<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { LEAFLINK_PLUS_ID, SPLIT_NAMES } from '@/constants';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { i18n } from '@/plugins';
  import { useProductsQuery } from '@/queries/feature-enrollment/feature-enrollment.query';

  import Container from './Container.vue';
  import StartFreeTrialButton from './StartFreeTrialButton.vue';
  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const isNueIntegrationEnabled = computed(() => {
    return getIsSplitEnabled.value(SPLIT_NAMES.ENROLLMENT_NUE_INTEGRATION);
  });

  const router = useRouter();
  const route = useRoute();
  const { userActiveCompany } = useUserStore();

  const isExiting = ref(false);

  const productsQuery = useProductsQuery({
    enabled: isNueIntegrationEnabled.value,
  });

  const isFreeTrial = computed(() => {
    const llPlusProduct = productsQuery.data.value?.find((product) => product.nueId === LEAFLINK_PLUS_ID);

    return llPlusProduct?.freeTrialUnit && llPlusProduct?.freeTrialUnit !== '0';
  });

  async function handleExiting() {
    isExiting.value = true;

    try {
      // When user comes from another LL page, return to that page
      if (route.query.previous && typeof route.query.previous === 'string') {
        router.push({ name: route.query.previous, params: { company: userActiveCompany?.slug } });
        return;
      }

      // When user follows an external link (e.g. a link in email), or lands directly on this page, go to dashboard
      await router.push(`/c/${userActiveCompany?.slug}/`);
    } finally {
      isExiting.value = true;
    }
  }
</script>

<template>
  <header class="header tw-bg-gradient-to-b tw-from-[#211953] tw-to-[#211953F2] tw-relative tw-z-control">
    <Button
      class="tw-absolute tw-top-4 tw-right-4 tw-text-white tw-scale-[2]"
      icon
      title="Exit"
      :is-loading="isExiting"
      @click="handleExiting"
    >
      <Icon name="close" />
    </Button>

    <Container
      class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-pt-[135px] tw-pb-[100px] tw-overflow-hidden tw-text-center"
    >
      <svg
        viewBox="0 0 52.917 8.413"
        xml:space="preserve"
        xmlns="http://www.w3.org/2000/svg"
        class="tw-w-[280px] md:tw-w-[380px] lg:tw-w-[480px]"
        data-test="header|logo"
      >
        <g fill="#fff">
          <path
            d="M9.486.019h1.044V6.46H9.486zM15.49 4.631h-3.253c.116.586.567.884 1.19.884.459 0 .891-.18 1.144-.523l.693.532c-.387.612-1.126.937-1.892.937-1.261 0-2.216-.883-2.216-2.199 0-1.342 1.009-2.19 2.207-2.19 1.208 0 2.154.839 2.154 2.154-.002.135-.01.27-.027.405zm-1.019-.73c-.063-.576-.504-.91-1.09-.91-.568 0-1.018.27-1.144.91zM20.611 2.163V6.46h-1.045v-.658c-.288.45-.883.658-1.398.658-1.108 0-2.073-.856-2.073-2.199 0-1.351.955-2.19 2.063-2.19.532 0 1.119.208 1.406.649v-.56zm-1.063 2.09c0-.739-.613-1.207-1.216-1.207-.649 0-1.18.504-1.18 1.207 0 .703.531 1.225 1.18 1.225.64 0 1.216-.486 1.216-1.225zM22.593 1.883v.37h.937v.838h-.937v3.37H21.55V1.937C21.55.73 22.125 0 23.296 0h.496l-.288.973c-.712 0-.92.397-.91.91zM24.526.02h1.045v6.44h-1.045zM26.358.766c0-.37.316-.64.685-.64.378 0 .676.27.676.64 0 .36-.298.64-.676.64-.369 0-.685-.28-.685-.64zm.163 1.487h1.045V6.46h-1.045zM32.529 4.1v2.36h-1.045V4.2c0-.667-.406-1.01-.874-1.01-.478 0-1.072.262-1.072 1.072V6.46h-1.047V2.253h1.046v.666c.216-.522.874-.756 1.306-.756 1.082 0 1.686.711 1.686 1.937zM36.063 6.46l-1.64-1.91v1.91h-1.045V.02h1.045v3.576l1.207-1.342h1.361L35.279 4.09l2.108 2.37zM1.942 5.147V3.912L0 6.46h1.128l.832-1.09a1.376 1.376 0 0 1-.018-.222zM3.965 6.08a1.014 1.014 0 0 1-1.01-1.015l.003-3.735A.924.924 0 0 1 4.514.667a1.317 1.317 0 0 0-2.456.665v3.81c0 .69.568 1.313 1.318 1.313a1.347 1.347 0 0 0 1.013-.47.996.996 0 0 1-.424.095z"
          />
          <path
            d="M4.848 5.144V3.91L3.71 5.393a.42.42 0 0 1-.518.122.888.888 0 0 0 1.462.12l.212-.276a1.296 1.296 0 0 1-.018-.215ZM6.872 6.08a1.014 1.014 0 0 1-1.01-1.015l.003-3.735A.928.928 0 0 1 7.42.668a1.317 1.317 0 0 0-2.455.664v3.81c0 .69.567 1.313 1.318 1.313a1.355 1.355 0 0 0 1.013-.47.997.997 0 0 1-.424.095z"
          />
          <path
            d="m7.026 4.851-.41.542a.42.42 0 0 1-.518.122.889.889 0 0 0 1.462.12l.181-.237zM6.79.544a.812.812 0 0 0-.716.43.42.42 0 0 1 .542.622l-.637.835v1.476l1.35-1.765a1.3 1.3 0 0 0 .272-.798.81.81 0 0 0-.811-.8ZM3.884.544a.812.812 0 0 0-.716.43.42.42 0 0 1 .542.622l-.637.835v1.476l1.35-1.765c.177-.229.272-.51.271-.798a.81.81 0 0 0-.81-.8Z"
          />
        </g>
        <path
          d="M121.957 146.025c1.09 0 2.038.851 2.038 2.18 0 1.322-.94 2.172-2.03 2.172-.522 0-1.098-.212-1.382-.655v2.57h-1.028v-6.16h1.028v.55c.284-.444.877-.657 1.374-.657zm-.16 3.395c.638 0 1.17-.523 1.17-1.214 0-.683-.532-1.223-1.17-1.223-.63 0-1.196.496-1.196 1.223 0 .736.602 1.214 1.196 1.214zm2.91.851v-6.39h1.029v6.39zm4.875-4.14h1.028v4.14h-1.028v-.647c-.213.523-.869.753-1.286.753-1.063 0-1.657-.717-1.657-1.923v-2.322h1.028v2.278c0 .665.399 1.01.86 1.01.47 0 1.055-.266 1.055-1.072zm3.369 4.246c-.825 0-1.65-.496-1.667-1.382h.966c0 .283.248.55.683.55.408 0 .673-.205.673-.47 0-.23-.177-.337-.487-.408l-.55-.142c-.93-.248-1.17-.745-1.17-1.268 0-.647.639-1.232 1.543-1.232.735 0 1.569.373 1.56 1.295h-.984c0-.284-.257-.461-.55-.461-.319 0-.54.186-.54.443 0 .213.203.337.443.39l.665.177c.93.24 1.099.824 1.099 1.206 0 .842-.842 1.302-1.684 1.302z"
          fill="#8389d1"
          transform="translate(-81.718 -143.88)"
        />
      </svg>

      <h1 class="tw-px-4 tw-my-5 tw-text-5xl tw-leading-normal tw-text-white">
        {{ i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.header.title') }}
      </h1>

      <p class="tw-text-xl tw-text-white tw-px-8 lg:tw-px-12">
        {{ i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.header.description') }}
      </p>

      <StartFreeTrialButton
        class="tw-w-[300px] tw-mt-4"
        :is-loading="productsQuery.isFetching.value"
        :location="'Header'"
      >
        {{ isFreeTrial ? i18n.t('inventoryInsights.startFreeTrial') : i18n.t('common.activate') }}
      </StartFreeTrialButton>
    </Container>

    <div class="clipart-flag tw-hidden xl:tw-block" />
    <div class="clipart-ring tw-hidden xl:tw-block" />
    <div class="clipart-pill tw-hidden xl:tw-block" />
  </header>
</template>

<style scoped>
  .clipart-flag {
    position: absolute;
    top: 0;
    left: 50%;
    width: 220px;
    height: 90px;
    transform: translateX(303px);

    @apply tw-bg-teal-500;

    &::before {
      position: absolute;
      top: 100%;
      border-width: 110px;
      border-style: solid;
      border-color: transparent;
      box-sizing: content-box;
      content: '';

      @apply tw-border-t-teal-500;
    }
  }

  .clipart-ring {
    --size: 165px;

    position: absolute;
    top: 16px;
    left: -182px;
    width: var(--size);
    height: var(--size);
    border-width: 85px;
    border-style: solid;
    border-radius: 100%;
    box-sizing: content-box;

    @apply tw-border-teal-500;
  }

  .clipart-pill {
    --width: 368px;
    --height: 184px;

    position: absolute;
    right: -100px;
    bottom: calc(var(--height) / 2 * -1);
    width: var(--width);
    height: var(--height);
    border-radius: calc(var(--height) / 2);

    @apply tw-bg-teal-500;
  }
</style>
